import ClearIcon from "@assets/images/Clear.svg";
import { Box, Paper, PaperProps, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import useDebounce from "@saleor/hooks/useDebounce";
import { makeStyles } from "@saleor/macaw-ui";
import React, { useEffect, useRef, useState } from "react";

import MyTypography from "../MyTypography";
import HandleRenderOption from "./RenderOption";

interface MakeStyleProps {
  contained?: boolean;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      "& .MuiAutocomplete-clearIndicator": {
        border: 0
      },
      "& .MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root']": {
        padding: "0 39px 0 0"
      },
      '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input:first-child': {
        paddingLeft: "1.6rem"
      },
      "& .Mui-disabled": {
        background: theme.palette.grey[200]
      },
      "& .Mui-disabled input": {
        padding: "0 !important",
        background: theme.palette.grey[200]
      }
    },
    paper: {
      width: "100%",
      maxWidth: (props: MakeStyleProps) => props.contained && "100%",
      background: "white",
      boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.12)"
    },
    paperItem: {
      fontSize: "1.4rem",
      lineHeight: "2.2rem",
      color: theme.palette.grey[900],
      fontFamily: "ScgFont, sans-serif",
      width: "100%",
      boxShadow: "0 0 0 0",
      borderRadius: 0,
      '& li[data-focus="true"]': {
        color: theme.palette.info.main,
        backgroundColor: theme.palette.background.default
      },
      '& li[aria-selected="true"]': {
        color: theme.palette.info.main,
        backgroundColor: "#EBF1FF"
      },
      "& .MuiAutocomplete-listbox": {
        "&::-webkit-scrollbar": {
          height: "14px",
          background: "#FFFFFF"
        },
        "&::-webkit-scrollbar-track": {
          borderRadius: 10
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#CCCCCC",
          borderRadius: 10,
          border: "4px solid #FFFFFF",
          height: "6px"
        }
      }
    },
    paperHead: {
      background: "white",
      padding: 16
    },
    divider: {
      margin: "0 16px",
      borderTop: `1px solid ${theme.palette.grey[300]}`
    }
  }),
  { name: "Autocomplete" }
);

interface IAutoComplete {
  optionsProp: any[];
  className?: any;
  placeholderProps?: string;
  onChangeProps?: (value: string) => void;
  loading?: boolean;
  onSearch?: (value: string) => void;
  handleSearch?: () => void;
  isReset?: boolean;
  resetAlways?: boolean;
  contained?: boolean;
  error?: boolean;
  disabled?: boolean;
  initialValue?: string;
  maxLengthProps?: number;
  numberOnly?: boolean;
  notUseFilterOptions?: boolean;
  id?: string;
  testId?: string;
  name?: string;
}

const AutoCompleteComponent: React.FC<IAutoComplete> = props => {
  const {
    optionsProp,
    placeholderProps,
    onChangeProps,
    loading,
    onSearch,
    resetAlways,
    isReset,
    error,
    disabled,
    handleSearch,
    initialValue,
    maxLengthProps,
    numberOnly,
    notUseFilterOptions,
    id,
    testId,
    name
  } = props;
  const classes = useStyles(props);
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState<string>("");
  const [valueDebounce, setValueDebounce] = useState<string>("");
  const [valueSelected, setValueSelected] = useState<string>("");
  const valueDebounces = useDebounce(setValueDebounce, 500);

  const CustomPaper = (props: PaperProps) => {
    const checkDataMatchKeyword =
      props?.children?.[(props?.children as any[])?.length - 1];
    return (
      <Box className={classes.paper}>
        <Box className={classes.paperHead}>
          <MyTypography variant="body3" style={{ color: "#696969" }}>
            {!loading
              ? value.length >= 2 || valueSelected
                ? optionsProp.length && checkDataMatchKeyword
                  ? `Suggested searches (${optionsProp.length})`
                  : `No results found`
                : "Please enter 2 or more characters"
              : ""}
          </MyTypography>
        </Box>
        {(value.length >= 2 || valueSelected) && checkDataMatchKeyword && (
          <>
            <Box className={classes.divider}></Box>
            <Paper elevation={8} {...props} className={classes.paperItem} />
          </>
        )}
      </Box>
    );
  };

  const handleOnChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setValue(e.target.value || "");
  };

  const handleOnKeyPress: React.KeyboardEventHandler<HTMLDivElement> = e => {
    if (inputRef.current.value === "" && e.key === " ") {
      e.preventDefault();
    }
    if (maxLengthProps && inputRef.current.value.length >= maxLengthProps) {
      e.preventDefault();
    }
  };

  const handleOnPaste = e => {
    const currentValue = e.clipboardData.getData("Text");
    if (!currentValue) {
      e.preventDefault();
      return;
    }
    if (numberOnly && !new RegExp("^[0-9]*$").test(currentValue)) {
      e.preventDefault();
      return;
    }
    if (maxLengthProps) {
      if (currentValue.length > maxLengthProps) {
        e.preventDefault();
        return;
      }
    }
    setValue(currentValue);
    // if (
    //   !currentValue ||
    //   (new RegExp("^[0-9]*$").test(currentValue) &&
    //     (currentValue.length <= maxLengthProps || !maxLengthProps))
    // ) {
    //   setValue(currentValue);
    // } else {
    //   event.preventDefault();
    // }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = e => {
    if (e.key === "Enter" && handleSearch) {
      handleSearch();
    }
  };

  useEffect(() => {
    valueDebounces(value);
  }, [value]);

  useEffect(() => {
    if (onChangeProps) {
      onChangeProps(valueDebounce);
    }
  }, [valueDebounce]);

  useEffect(() => {
    if (onSearch) {
      onSearch(valueSelected || "");
    }
  }, [valueSelected]);

  useEffect(() => {
    if (isReset || resetAlways) {
      setValueSelected("");
      setValue("");
      setValueDebounce("");
    }
  }, [isReset]);

  const extraProps = {
    ...(notUseFilterOptions && {
      filterOptions: option => option
    })
  };

  return (
    <div className={classes.root}>
      <Autocomplete
        onChange={(_event, value, _reason) => {
          setValueSelected(value);
        }}
        {...extraProps}
        freeSolo
        key={`free-solo-demo-${isReset}`}
        options={optionsProp.map(option => option.title)}
        id={id}
        data-testid={testId}
        data-input-type="autocomplete-field"
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={placeholderProps}
            value={value}
            onChange={handleOnChange}
            onKeyPress={handleOnKeyPress}
            onKeyDown={handleKeyDown}
            inputRef={inputRef}
            error={error}
            id={id}
            name={name}
            inputProps={{
              ...params.inputProps,
              "data-testid": `${testId}-input`
            }}
          />
        )}
        closeIcon={
          <img src={ClearIcon} data-testid={`${testId}-clearIcon`} alt="" />
        }
        PaperComponent={CustomPaper}
        loading={loading}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === "reset" || reason === "clear") {
            setValue("");
            setValueSelected("");
            setValueDebounce("");
            return;
          } else {
            setValue(newInputValue?.trim());
          }
        }}
        onPaste={handleOnPaste}
        value={valueSelected || initialValue}
        renderOption={HandleRenderOption}
        disabled={disabled}
        ListboxProps={{ style: { maxHeight: 280, overflow: "auto" } }}
      />
    </div>
  );
};
export default AutoCompleteComponent;
